import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import { lazy, Suspense, useState, useEffect } from 'react';
// CookieBanner est chargé APRES le premier paint (requestIdleCallback) :
// il importe framer-motion — en le différant, framer-motion sort du bundle
// d'entrée (~201 Ko) et ne bloque plus le FCP/LCP sur mobile.
const CookieBanner = lazy(() => import('@/components/CookieBanner'));
const Home = lazy(() => import('@/pages/Home'));
import ProtectedRoute from '@/components/ProtectedRoute';
import LanguageSync from '@/components/LanguageSync';
import { Navigate } from 'react-router-dom';

// Code-splitting : seules les routes visitées sont chargées (réduit drastiquement le JS initial).
const Tarifs = lazy(() => import('@/pages/Tarifs'));
const Simulateur = lazy(() => import('@/pages/Simulateur'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const SecuriteRGPD = lazy(() => import('@/pages/SecuriteRGPD'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogArticlePage = lazy(() => import('@/pages/BlogArticlePage'));
const BlogAdmin = lazy(() => import('@/pages/BlogAdmin'));
const MediaLibrary = lazy(() => import('@/pages/MediaLibrary'));
const SimulateurStats = lazy(() => import('@/pages/admin/SimulateurStats'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const OAuthConsent = lazy(() => import('@/pages/OAuthConsent'));
const Cookies = lazy(() => import('@/pages/Cookies'));
const SecuriteIA = lazy(() => import('@/pages/SecuriteIA'));
const AgentBuilder = lazy(() => import('@/pages/AgentBuilder'));
const Confidentialite = lazy(() => import('@/pages/legal/Confidentialite'));
const Recrutement = lazy(() => import('@/pages/Recrutement'));
const RecrutementPoste = lazy(() => import('@/pages/RecrutementPoste'));

const PageSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-[#F2F9FD]">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-[#0985D8] rounded-full animate-spin"></div>
  </div>
);

// Monte la bannière cookie (et son chunk framer-motion) au premier geste
// utilisateur (filet 8 s). L'ancien déclencheur « idle » la faisait apparaître
// vers ~6 s dans un audit Lighthouse (aucune interaction en labo) et son
// paragraphe devenait l'élément LCP → LCP mesuré à 6,8 s. Un visiteur réel
// bouge/scrolle en < 1 s (ce qui fige la mesure LCP) : aucun changement perçu,
// et le consent mode reste « refusé » par défaut tant que rien n'est choisi.
function DeferredCookieBanner() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let done = false;
    const events = ["pointermove", "pointerdown", "scroll", "keydown", "touchstart"];
    const arm = () => {
      if (done) return;
      done = true;
      events.forEach((e) => window.removeEventListener(e, arm));
      clearTimeout(timer);
      setReady(true);
    };
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    const timer = setTimeout(arm, 8000);
    return () => {
      done = true;
      events.forEach((e) => window.removeEventListener(e, arm));
      clearTimeout(timer);
    };
  }, []);
  if (!ready) return null;
  return <Suspense fallback={null}><CookieBanner /></Suspense>;
}

const AuthenticatedApp = () => {
  const { authError, navigateToLogin } = useAuth();

  // Note : on ne bloque pas le rendu sur isLoadingPublicSettings/isLoadingAuth.
  // Le site est public — le Hero doit peindre immédiatement (LCP). Le gate
  // d'auth des pages protégées est géré par <ProtectedRoute> (checkUserAuth).
  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Suspense fallback={<PageSpinner />}>
    <Routes>
      {/* Add your page Route elements here */}
      <Route path="/" element={<Home />} />
      {/* ── Miroirs /en/ (hreflang) — mêmes composants, langue pilotée par LanguageSync ── */}
      <Route path="/en" element={<Home />} />
      <Route path="/en/tarifs" element={<Tarifs />} />
      <Route path="/tarifs" element={<Tarifs />} />
      <Route path="/simulateur" element={<Simulateur />} />
      <Route path="/a-propos" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/securite-rgpd" element={<SecuriteRGPD />} />
      <Route path="/securite-ia" element={<SecuriteIA />} />
      <Route path="/agent-builder" element={<AgentBuilder />} />
      <Route path="/en/agent-builder" element={<AgentBuilder />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/en/simulateur" element={<Simulateur />} />
      <Route path="/en/a-propos" element={<About />} />
      <Route path="/en/contact" element={<Contact />} />
      <Route path="/en/securite-rgpd" element={<SecuriteRGPD />} />
      <Route path="/en/securite-ia" element={<SecuriteIA />} />
      <Route path="/en/cookies" element={<Cookies />} />
      <Route path="/en/confidentialite" element={<Confidentialite />} />
      <Route path="/en/blog" element={<Blog />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/article/:slug" element={<BlogArticlePage />} />
      <Route path="/recrutement" element={<Recrutement />} />
      <Route path="/recrutement/:slug" element={<RecrutementPoste />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/oauth/consent" element={<OAuthConsent />} />
      <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
        <Route path="/admin/blog" element={<BlogAdmin />} />
        <Route path="/admin/mediatheque" element={<MediaLibrary />} />
        <Route path="/admin/simulateur-stats" element={<SimulateurStats />} />
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <LanguageSync />
          <AuthenticatedApp />
          <DeferredCookieBanner />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App